import { Checkbox } from '@nimbus-ds/components';
import {
  PaymentMethodDiscountDto,
  UpdatePaymentProviderSettingsRequestDto,
} from '@tiendanube/common';
import {
  BaseCard,
  InterfaceNameValue,
  Stack,
  Text,
  Title,
} from '@tiendanube/components';
import { InputNumber } from 'commons/components';
import { InterfaceNameBooleanValue } from 'commons/types';
import PercentageOffDiscountAlert from 'domains/Payments/components/PercentageOffDiscountAlert';
import useTranslationPayments from 'domains/Payments/useTranslationPayments';

const EXTERNAL_CHECKOUT_KEY = 'redirect';

interface DiscountsCardProps {
  discounts: PaymentMethodDiscountDto[];
  values: UpdatePaymentProviderSettingsRequestDto;
  errors: Partial<Record<string, string>>;
  handleOnChange: (data: InterfaceNameBooleanValue) => void;
  handleOnChangeGroup: (
    group: string,
    data: InterfaceNameValue | InterfaceNameBooleanValue,
  ) => void;
}

function DiscountsCard({
  discounts,
  values,
  errors,
  handleOnChange,
  handleOnChangeGroup,
}: Readonly<DiscountsCardProps>): JSX.Element {
  const t = useTranslationPayments();

  const handleChangeDiscount = (data: InterfaceNameValue) => {
    handleOnChangeGroup('discounts', data);
  };

  const handleAllowDiscountCombination = () => {
    handleOnChange({
      name: 'allowDiscountCombination',
      value: !values.allowDiscountCombination,
    });
  };

  const transparentCheckoutDiscounts = discounts.filter(
    ({ paymentMethodType }) => paymentMethodType !== EXTERNAL_CHECKOUT_KEY,
  );
  const externalCheckoutDiscount = discounts.find(
    ({ paymentMethodType }) => paymentMethodType === EXTERNAL_CHECKOUT_KEY,
  );

  const anyDiscountsHavePercentageOff = discounts.some(
    (discount) => discount.hasPercentageOffDiscount,
  );
  const allDiscountsHavePercentageOff = discounts.every(
    (discount) => discount.hasPercentageOffDiscount,
  );

  return (
    <BaseCard>
      <BaseCard.Header>
        <Title type="h3">{`${t('paymentProvider.settings.discounts')}`}</Title>
      </BaseCard.Header>
      <BaseCard.Body>
        <Stack column align="stretch">
          {anyDiscountsHavePercentageOff && (
            <PercentageOffDiscountAlert isGeneral />
          )}
          {transparentCheckoutDiscounts.length > 0 && (
            <>
              <Text size="highlight">
                {t('paymentProvider.settings.transparentCheckout')}
              </Text>
              {transparentCheckoutDiscounts?.map((discount) => (
                <Stack key={discount.paymentMethodType} column align="stretch">
                  <InputNumber
                    type="integer"
                    name={discount.paymentMethodType}
                    label={t(
                      `paymentProvider.method.${discount.paymentMethodType}`,
                    )}
                    appendLabel="%"
                    max="100"
                    value={
                      discount.hasPercentageOffDiscount
                        ? ''
                        : values.discounts?.[discount.paymentMethodType] || ''
                    }
                    disabled={discount.hasPercentageOffDiscount}
                    onChange={handleChangeDiscount}
                    helpText={t(
                      errors?.[`discounts[${discount.paymentMethodType}]`] ||
                        '',
                    )}
                    appearance={
                      errors?.[`discounts[${discount.paymentMethodType}]`]
                        ? 'validation_error'
                        : 'default'
                    }
                  />
                </Stack>
              ))}
            </>
          )}
          {!!externalCheckoutDiscount && (
            <>
              <Text size="highlight">
                {t('paymentProvider.method.redirect')}
              </Text>
              <InputNumber
                type="integer"
                name={EXTERNAL_CHECKOUT_KEY}
                appendLabel="%"
                max="100"
                value={
                  externalCheckoutDiscount.hasPercentageOffDiscount
                    ? ''
                    : values.discounts?.[EXTERNAL_CHECKOUT_KEY] || ''
                }
                disabled={externalCheckoutDiscount.hasPercentageOffDiscount}
                onChange={handleChangeDiscount}
                helpText={t(
                  errors?.[`discounts[${EXTERNAL_CHECKOUT_KEY}]`] || '',
                )}
                appearance={
                  errors?.[`discounts[${EXTERNAL_CHECKOUT_KEY}]`]
                    ? 'validation_error'
                    : 'default'
                }
              />
            </>
          )}
          {!allDiscountsHavePercentageOff && (
            <Checkbox
              name="allowDiscountCombination"
              checked={values.allowDiscountCombination}
              label={t('paymentProvider.settings.allowDiscountCombination')}
              onChange={handleAllowDiscountCombination}
            />
          )}
        </Stack>
      </BaseCard.Body>
    </BaseCard>
  );
}

export default DiscountsCard;
